import { useEffect, useRef } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Defers input focus until after the container's enter animation completes.
 *
 * iOS Safari/Chrome doesn't trigger native keyboard scroll behavior for synchronous
 * programmatic focus. Deferring the focus with a delay lets iOS treat it more like
 * a user-initiated focus and correctly scroll the input into view above the keyboard.
 *
 * `isActive` controls whether the focus effect runs. Pass `true` when the input is
 * visible (e.g. the dialog is open, or the gate's current step is mounted); pass
 * `false` to defer focusing until visibility flips on. Re-focus triggers whenever
 * the flag transitions to `true`.
 */
export const useDeferredInputFocus = <T extends HTMLElement = HTMLInputElement>(isActive: boolean = true) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!isActive) return;

    const timer = withWindow(
      global =>
        global.setTimeout(() => {
          ref.current?.focus();
        }, 300),
      undefined,
    );
    return () => withWindow(global => global.clearTimeout(timer));
  }, [isActive]);

  return ref;
};
